/**
 * PreviewBookingFlow — animated adapter over the real production booking flow shell.
 *
 * Reuses the same production components for service selection, date/time,
 * customer details, and confirmation. Local mock state only: no Stripe,
 * availability, or booking writes.
 */

import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { BookingFlowShell } from '@/components/booking-flow/BookingFlowShell';
import { type BuildRowData } from '@/components/booking-flow/FlowScaffold';
import { type PassBooking } from '@/components/booking-flow/ConfirmationPass';
import { formatDuration } from '@/components/booking-flow/FlowSteps';
import { type BookingTheme } from '@/components/booking-flow/themes';
import ServiceSelectionStep from '@/components/magic-flow/ServiceSelectionStep';
import DateTimeSelectionStep from '@/components/magic-flow/DateTimeSelectionStep';
import PreviewCheckoutStep from './PreviewCheckoutStep';
import type { DraftService } from '@/types/draft';

export interface PreviewBookingFlowHandle {
  restart: () => void;
}

interface PreviewBookingFlowProps {
  theme: BookingTheme;
  businessName: string;
  providerName: string;
  services: DraftService[];
  onBack: () => void;
  autoPlay?: boolean;
  onComplete?: () => void;
}

interface FlowService {
  id: string;
  name: string;
  description: string;
  price: number;
  duration: number;
  specialRequirements?: string;
}

const TOTAL_STEPS = 4;
const STEP_HEADERS: Record<number, string> = {
  1: 'Select a service',
  2: 'Choose a time',
  3: 'Your details',
};

function nextFridayAtTwo(): Date {
  const now = new Date();
  const result = new Date(now);
  const daysUntilFriday = (5 - now.getDay() + 7) % 7 || 7;
  result.setDate(now.getDate() + daysUntilFriday);
  result.setHours(14, 0, 0, 0);
  return result;
}

const PreviewBookingFlow = forwardRef<PreviewBookingFlowHandle, PreviewBookingFlowProps>(function PreviewBookingFlow({
  theme,
  businessName,
  providerName,
  services,
  onBack,
  autoPlay = false,
  onComplete,
}, ref) {
  const [step, setStep] = useState(1);
  const [selectedService, setSelectedService] = useState<FlowService | null>(null);
  const [selectedDateTime, setSelectedDateTime] = useState<{ date: Date; time: string } | null>(null);
  const [confirmStage, setConfirmStage] = useState<'confirming' | 'tray' | 'expanded'>('confirming');
  const [runId, setRunId] = useState(0);
  const onCompleteRef = useRef(onComplete);
  const completedRunRef = useRef<number | null>(null);

  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  const flowServices: FlowService[] = useMemo(() => services.map((service) => ({
    id: service.id,
    name: service.name,
    description: service.description || '',
    price: service.price,
    duration: service.duration,
  })), [services]);

  const restart = useCallback(() => {
    setRunId((current) => current + 1);
  }, []);

  useImperativeHandle(ref, () => ({ restart }), [restart]);

  const goNext = useCallback(() => {
    setStep((current) => Math.min(current + 1, TOTAL_STEPS));
  }, []);

  const goPrev = useCallback(() => {
    if (step > 1) {
      setStep((current) => current - 1);
    } else {
      onBack();
    }
  }, [step, onBack]);

  const handleSelectService = useCallback((service: FlowService) => {
    setSelectedService(service);
    window.setTimeout(() => setStep(2), 360);
  }, []);

  useEffect(() => {
    if (!autoPlay || !flowServices.length) return;

    completedRunRef.current = null;
    setStep(1);
    setSelectedService(null);
    setSelectedDateTime(null);
    setConfirmStage('confirming');

    const selected = flowServices[0];
    const picked = nextFridayAtTwo();
    const timers = [
      window.setTimeout(() => setSelectedService(selected), 900),
      window.setTimeout(() => setStep(2), 1260),
      window.setTimeout(() => setSelectedDateTime({ date: picked, time: '2:00 PM' }), 2450),
      window.setTimeout(() => setStep(3), 3700),
      window.setTimeout(() => setStep(4), 6100),
      window.setTimeout(() => setConfirmStage('tray'), 7600),
      window.setTimeout(() => setConfirmStage('expanded'), 8500),
      window.setTimeout(() => {
        if (completedRunRef.current === runId) return;
        completedRunRef.current = runId;
        onCompleteRef.current?.();
      }, 9000),
    ];

    return () => timers.forEach(window.clearTimeout);
  }, [autoPlay, flowServices, runId]);

  useEffect(() => {
    if (step !== 4 || autoPlay) return;
    setConfirmStage('confirming');
    const timer = window.setTimeout(() => setConfirmStage('tray'), 1500);
    return () => window.clearTimeout(timer);
  }, [step, autoPlay]);

  const mockBooking: PassBooking | null = selectedService && selectedDateTime
    ? {
        service: selectedService.name,
        dateNum: selectedDateTime.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        dateDay: selectedDateTime.date.toLocaleDateString('en-US', { weekday: 'long' }),
        time: selectedDateTime.time,
        duration: formatDuration(selectedService.duration),
        guest: null,
        confirmation: 'FIGGY-PREVIEW',
      }
    : null;

  const buildRows: BuildRowData[] = [];
  if (selectedService) {
    buildRows.push({
      label: 'Service',
      value: selectedService.name,
      sub: `${formatDuration(selectedService.duration)} · $${selectedService.price}`,
      state: 'filled',
    });
  }
  if (selectedDateTime) {
    buildRows.push({
      label: 'When',
      value: `${selectedDateTime.date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })} · ${selectedDateTime.time}`,
      state: 'filled',
    });
  }

  let stepContent: React.ReactNode = null;
  if (step === 1) {
    stepContent = (
      <ServiceSelectionStep
        selectedService={selectedService}
        onSelectService={handleSelectService}
        services={flowServices}
      />
    );
  } else if (step === 2 && selectedService) {
    stepContent = (
      <DateTimeSelectionStep
        serviceDuration={selectedService.duration}
        selectedDateTime={selectedDateTime}
        onSelectDateTime={setSelectedDateTime}
      />
    );
  } else if (step === 3 && selectedService) {
    stepContent = <PreviewCheckoutStep service={selectedService} />;
  }

  const showDock = step < 4;
  const ctaDisabled = step === 1 ? !selectedService : step === 2 ? !selectedDateTime : false;
  const cta = step === 1
    ? 'Continue'
    : step === 2
      ? 'Continue'
      : selectedService
        ? `Confirm booking · $${selectedService.price}`
        : 'Continue';
  const ctaSub = step === 1
    ? selectedService
      ? `${formatDuration(selectedService.duration)} · $${selectedService.price}`
      : 'Select a service to continue'
    : step === 2
      ? selectedDateTime
        ? `${selectedDateTime.date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })} · ${selectedDateTime.time}`
        : 'Choose a date and time'
      : 'Preview — nothing will be charged';

  return (
    <BookingFlowShell
      theme={theme}
      businessName={providerName || businessName}
      step={Math.min(step, 3)}
      totalSteps={3}
      title={STEP_HEADERS[Math.min(step, 3)] ?? STEP_HEADERS[1]}
      buildRows={buildRows}
      onBack={goPrev}
      showDock={showDock}
      cta={cta}
      ctaDisabled={ctaDisabled}
      ctaSub={ctaSub}
      onCta={step === 1 ? () => selectedService && goNext() : goNext}
      noSnapshotKeys={['step-2', 'step-3']}
      showConfirmation={step === 4}
      confirmStage={confirmStage}
      confirmBooking={mockBooking}
      onConfirmTap={() => setConfirmStage((current) => (current === 'tray' ? 'expanded' : current))}
      onConfirmClose={() => setConfirmStage('tray')}
    >
      {stepContent}
    </BookingFlowShell>
  );
});

export default PreviewBookingFlow;
